// src/components/HealthCheckPanel.tsx
import React, { useCallback, useMemo, useState } from "react";
import { Connection, PublicKey, SystemProgram } from "@solana/web3.js";

import { SOLANA_ENDPOINT } from "../runtime-config";
import { getDefaultProgramId, PhantomProvider } from "../solana/roundsClient";
import { derivePoolPdaV2, deriveVaultPdaV2, deriveMemberPdaV2 } from "../solana/weareSusuPdas";

type Status = "PASS" | "WARN" | "FAIL";

type CheckRow = {
  status: Status;
  label: string;
  detail: string;
};

function badgeStyle(status: Status): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    marginRight: 8,
    border: "1px solid #ddd",
  };
  if (status === "PASS") return { ...base, background: "#eaffea", borderColor: "#9bd49b" };
  if (status === "WARN") return { ...base, background: "#fff7e6", borderColor: "#ffd18a" };
  return { ...base, background: "#ffecec", borderColor: "#ff9a9a" };
}

function short(pk?: string) {
  if (!pk) return "—";
  return `${pk.slice(0, 6)}…${pk.slice(-6)}`;
}

function getPhantom(): PhantomProvider | null {
  const w = window as any;
  if (w?.phantom?.solana?.isPhantom) return w.phantom.solana as PhantomProvider;
  return null;
}

function asPublicKey(maybe: any): PublicKey | null {
  try {
    if (!maybe) return null;
    if (maybe instanceof PublicKey) return maybe;
    if (typeof maybe === "string") return new PublicKey(maybe);
    if (typeof maybe?.toBase58 === "function") return new PublicKey(maybe.toBase58());
    return new PublicKey(String(maybe));
  } catch {
    return null;
  }
}

// --------------------
// Minimal binary decode (Anchor account data)
// --------------------
function readPubkey(buf: Uint8Array, offset: number): PublicKey {
  return new PublicKey(buf.slice(offset, offset + 32));
}
function readU8(buf: Uint8Array, offset: number): number {
  return buf[offset] ?? 0;
}
function readBool(buf: Uint8Array, offset: number): boolean {
  return (buf[offset] ?? 0) !== 0;
}
function readU32LE(buf: Uint8Array, offset: number): number {
  return (
    (buf[offset] ?? 0) |
    ((buf[offset + 1] ?? 0) << 8) |
    ((buf[offset + 2] ?? 0) << 16) |
    ((buf[offset + 3] ?? 0) << 24)
  ) >>> 0;
}
function readU64LE(buf: Uint8Array, offset: number): bigint {
  let x = 0n;
  for (let i = 0; i < 8; i++) {
    x |= BigInt(buf[offset + i] ?? 0) << (8n * BigInt(i));
  }
  return x;
}

// PoolV2 layout inferred from on-chain account size (144 bytes total = 8 disc + 136 payload)
function decodePoolV2(data: Uint8Array) {
  // skip 8-byte discriminator
  const base = 8;

  const authority = readPubkey(data, base + 0);
  const operator = readPubkey(data, base + 32);
  const vault = readPubkey(data, base + 64);
  const vaultSeedVersion = readU8(data, base + 96);
  const deprecated = readBool(data, base + 97);

  // bytes base+98..base+103 are padding/alignment
  const round = readU64LE(data, base + 104);
  const memberCount = readU32LE(data, base + 112);
  const currentIndex = readU32LE(data, base + 116);
  const totalContributions = readU64LE(data, base + 120);
  const lastPayoutRound = readU64LE(data, base + 128);

  return {
    authority,
    operator,
    vault,
    vaultSeedVersion,
    deprecated,
    round,
    memberCount,
    currentIndex,
    totalContributions,
    lastPayoutRound,
  };
}

export default function HealthCheckPanel() {
  const [rows, setRows] = useState<CheckRow[]>([]);
  const [running, setRunning] = useState(false);
  const [rawLog, setRawLog] = useState<string[]>([]);

  const pushLog = useCallback((line: string) => {
    setRawLog((prev) => [...prev, `${new Date().toISOString()} ${line}`]);
  }, []);

  const connection = useMemo(() => new Connection(SOLANA_ENDPOINT, "confirmed"), []);
  const programId = useMemo(() => new PublicKey(getDefaultProgramId()), []);

  const run = useCallback(async () => {
    setRunning(true);
    setRows([]);
    setRawLog([]);
    try {
      const out: CheckRow[] = [];

      // Wallet check (Phantom)
      const phantom = getPhantom();
      const walletPk = asPublicKey((phantom as any)?.publicKey);

      if (!phantom) {
        out.push({ status: "FAIL", label: "Wallet Provider", detail: "Phantom not detected in browser." });
      } else {
        out.push({ status: "PASS", label: "Wallet Provider", detail: "Phantom detected." });
      }

      if (!walletPk) {
        out.push({ status: "WARN", label: "Wallet Connected", detail: "No wallet public key yet. Click Connect Phantom first." });
      } else {
        out.push({ status: "PASS", label: "Wallet Connected", detail: `Wallet: ${walletPk.toBase58()} (${short(walletPk.toBase58())})` });
      }

      // RPC health
      pushLog(`RPC ping: ${SOLANA_ENDPOINT}`);
      try {
        const v = await connection.getVersion();
        out.push({ status: "PASS", label: "RPC Reachable", detail: `Solana RPC OK. Version: ${v["solana-core"] ?? "unknown"}` });
      } catch (e: any) {
        out.push({ status: "FAIL", label: "RPC Reachable", detail: `RPC failed: ${e?.message ?? String(e)}` });
        setRows(out);
        return;
      }

      // Program account health
      pushLog(`Program: ${programId.toBase58()}`);
      const prog = await connection.getAccountInfo(programId, "confirmed");
      if (!prog) {
        out.push({ status: "FAIL", label: "Program Account", detail: "Program account not found on this cluster." });
        setRows(out);
        return;
      }
      if (!prog.executable) {
        out.push({ status: "FAIL", label: "Program Executable", detail: "Program account exists but is not executable." });
        setRows(out);
        return;
      }
      out.push({ status: "PASS", label: "Program Executable", detail: `Executable program found. Owner: ${prog.owner.toBase58()}` });

      // If no wallet, we can’t do authority-scoped PDAs
      if (!walletPk) {
        out.push({ status: "WARN", label: "V2 PDA Derivation", detail: "Skipped (wallet not connected)." });
        setRows(out);
        return;
      }

      // Derive PDAs for V2 (authority-scoped)
      const [poolV2] = derivePoolPdaV2(programId, walletPk);
      const [vaultV2] = deriveVaultPdaV2(programId, poolV2);
      const [memberV2] = deriveMemberPdaV2(programId, poolV2, walletPk);

      out.push({ status: "PASS", label: "V2 PDAs Derived", detail: `PoolV2=${poolV2.toBase58()} VaultV2=${vaultV2.toBase58()} MemberV2=${memberV2.toBase58()}` });

      // Fetch accounts
      const [poolAi, vaultAi, memAi] = await Promise.all([
        connection.getAccountInfo(poolV2, "confirmed"),
        connection.getAccountInfo(vaultV2, "confirmed"),
        connection.getAccountInfo(memberV2, "confirmed"),
      ]);

      // Pool checks
      if (!poolAi) {
        out.push({ status: "FAIL", label: "PoolV2 Exists", detail: "PoolV2 account does not exist. (Create Pool V2 first.)" });
        setRows(out);
        return;
      }
      if (!poolAi.owner.equals(programId)) {
        out.push({ status: "FAIL", label: "PoolV2 Owner", detail: `PoolV2 owner mismatch. owner=${poolAi.owner.toBase58()} expected=${programId.toBase58()}` });
        setRows(out);
        return;
      }
      out.push({ status: "PASS", label: "PoolV2 Exists", detail: `PoolV2 exists. dataLen=${poolAi.data.length} lamports=${poolAi.lamports}` });

      // Decode PoolV2 (strict length expectation based on your testnet reality)
      if (poolAi.data.length !== 144) {
        out.push({
          status: "WARN",
          label: "PoolV2 Layout",
          detail: `Unexpected pool data length ${poolAi.data.length}. Expected 144 on your testnet deployment. Decode may be invalid.`,
        });
      } else {
        const decoded = decodePoolV2(poolAi.data);

        // Authority alignment
        if (decoded.authority.equals(walletPk)) {
          out.push({ status: "PASS", label: "PoolV2 Authority", detail: `Authority matches wallet: ${decoded.authority.toBase58()}` });
        } else {
          out.push({
            status: "WARN",
            label: "PoolV2 Authority",
            detail: `Authority != current wallet. authority=${decoded.authority.toBase58()} wallet=${walletPk.toBase58()}`,
          });
        }

        // Vault alignment
        if (decoded.vault.equals(vaultV2)) {
          out.push({ status: "PASS", label: "PoolV2 Vault", detail: `Stored vault matches derived VaultV2: ${decoded.vault.toBase58()}` });
        } else {
          out.push({
            status: "FAIL",
            label: "PoolV2 Vault",
            detail: `Stored vault does not match derived. stored=${decoded.vault.toBase58()} derived=${vaultV2.toBase58()}`,
          });
        }

        // Deprecated flag
        out.push({
          status: decoded.deprecated ? "WARN" : "PASS",
          label: "PoolV2 Deprecated",
          detail: decoded.deprecated ? "Pool is marked deprecated=true" : "deprecated=false",
        });

        out.push({
          status: "PASS",
          label: "PoolV2 Counters",
          detail: `round=${decoded.round.toString()} members=${decoded.memberCount} index=${decoded.currentIndex} total=${decoded.totalContributions.toString()} lastPayoutRound=${decoded.lastPayoutRound.toString()} seedVer=${decoded.vaultSeedVersion}`,
        });
      }

      // Vault checks (system-owned, dataLen=0)
      if (!vaultAi) {
        out.push({ status: "FAIL", label: "VaultV2 Exists", detail: "VaultV2 account does not exist." });
      } else {
        const ownerOk = vaultAi.owner.equals(SystemProgram.programId);
        const dataOk = vaultAi.data.length === 0;

        if (ownerOk && dataOk) {
          out.push({
            status: "PASS",
            label: "VaultV2 System-Owned",
            detail: `owner=SystemProgram, dataLen=0, lamports=${vaultAi.lamports}`,
          });
        } else {
          out.push({
            status: "FAIL",
            label: "VaultV2 System-Owned",
            detail: `Expected system-owned & dataLen=0. owner=${vaultAi.owner.toBase58()} dataLen=${vaultAi.data.length}`,
          });
        }
      }

      // Member checks
      if (!memAi) {
        out.push({ status: "WARN", label: "MemberV2 Exists", detail: "MemberV2 not found. (Join Pool V2 to create member.)" });
      } else if (!memAi.owner.equals(programId)) {
        out.push({
          status: "FAIL",
          label: "MemberV2 Owner",
          detail: `MemberV2 owner mismatch. owner=${memAi.owner.toBase58()} expected=${programId.toBase58()}`,
        });
      } else {
        out.push({ status: "PASS", label: "MemberV2 Exists", detail: `MemberV2 exists. dataLen=${memAi.data.length} lamports=${memAi.lamports}` });
      }

      setRows(out);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setRows([{ status: "FAIL", label: "Health Check Runtime", detail: msg }]);
      pushLog(`ERROR: ${msg}`);
    } finally {
      setRunning(false);
    }
  }, [connection, programId, pushLog]);

  return (
    <div style={{ padding: 16 }}>
      <h3>Health Check</h3>
      <div>RPC: {SOLANA_ENDPOINT}</div>
      <div>Program: {programId.toBase58()}</div>

      <div style={{ marginTop: 12 }}>
        <button disabled={running} onClick={run}>
          {running ? "Running…" : "Run Health Check"}
        </button>
      </div>

      <div style={{ marginTop: 12 }}>
        {rows.length === 0 ? (
          <div style={{ color: "#666" }}>— no checks run yet —</div>
        ) : (
          <div>
            {rows.map((r, i) => (
              <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid #eee" }}>
                <span style={badgeStyle(r.status)}>{r.status}</span>
                <strong>{r.label}</strong>
                <div style={{ marginLeft: 0, color: "#333" }}>{r.detail}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 700 }}>Debug Log</div>
        <pre style={{ whiteSpace: "pre-wrap", border: "1px solid #ccc", padding: 12 }}>
          {rawLog.length ? rawLog.join("\n") : "— (no logs yet) —"}
        </pre>
      </div>
    </div>
  );
}
